import type { PushSDK } from './push-sdk'
import type { PushSdkConfig, PushSdkConsentState } from './types/push-sdk-config'
import type { PushSDKLifecycleCallbacks } from './callbacks/push-sdk-lifecycle-callbacks'
import type { PermissionLifecycleCallbacks } from './callbacks/permission-lifecycle-callbacks'
import type { AppMessageLifecycleCallbacks } from './callbacks/app-message-lifecycle-callbacks'
import type { PromptLifecycleCallbacks } from './callbacks/prompt-lifecycle-callbacks.ts'
import {
    type ECommEventRecord,
    type ECommEventRecordWithRating,
    ECommEventRecordWithoutQuantity,
    type ECommProductRecord,
    type ECommProductRecordWithRating,
    ECommProductRecordWithoutQuantity,
    type ECommPurchaseDetails,
    type ECommPurchaseRecord,
    type ExternalIdCommandValue,
    type GetPromptingSuppressedStateCallback,
    type LoadCommandValue,
    type PageTagVisitCommandValue,
    type ProfileAppendCommandValue,
    type ProfileCommandValue,
    type ProfileRemoveCommandValue,
    type PushArguments,
    PushSdkCommand,
    type PushSdkContext,
    PushSdkEvent,
    type PushSdkEventCallback,
    type ShowPromptCommandValue,
    SuppressedAsyncCommands,
} from './types/push-sdk-legacy'
import { type Logger, TaggedLogger } from '@core/logging/logger'
import { type UserProfile, UserProfileImpl } from './modules/user-profile'
import { type PushNotifications, PushNotificationsImpl } from './modules/push-notifications.ts'
import { type EComm, ECommImpl } from './modules/ecomm.ts'
import { type Prompts, PromptsImpl } from './modules/prompts.ts'
import { PushSdkInternalImpl } from './push-sdk-internal-impl'
import { Globals } from './globals'
import { LogLevel } from '@core/logging/log-level'
import { getGlobalPushSdk } from './utils/validate-single-instance.ts'
import { SettingsStorageManager } from './storage/settings-storage-manager.ts'
import { UserStorageManager } from './storage/user-storage-manager.ts'
import { LegacyStorageManager } from './storage/legacy-storage-manager.ts'
import { EventManagerAsyncSettingsProvider } from './providers/event-manager-async-settings-provider.ts'
import { SessionManager } from './session/session-manager.ts'
import { SessionStorageManager } from './storage/session-storage-manager.ts'
import { Env } from './env.ts'
import { ensureError } from '@core/utils/ensure-error.ts'
import { TrackedEventAction } from '@core/events/tracked-event-action.ts'
import { DebugController } from './debug/debug-controller.ts'
import { StaticEventDispatcher } from '@core/static-event-dispatcher.ts'
import {
    type ECommItem,
    eCommItemOf,
    type ECommItemWithoutQuantity,
    type ECommItemWithQuantity,
} from './types/ecomm.ts'
import { ECommItemType } from './enums/e-comm-item-type.ts'
import { SdkEventManager } from './events/sdk-event-manager.ts'

function itemsWithQuantity(items: ECommItem[]): items is ECommItemWithQuantity[] {
    return items.every((item) => 'quantity' in item && typeof item.quantity === 'number')
}

function stripQuantity(items: ECommItem[]): ECommItemWithoutQuantity[] {
    return items.map(({ item_type, id }) => ({ item_type, id }))
}

/** @internal */
export class PushSdkPublicImpl implements PushSDK {
    /**
     * Singleton factory method returns the [global]{@link getGlobalPushSdk}
     * instance of the {@link PushSDK} or instantiates
     * a new one.
     */
    public static getInstance(): PushSDK {
        const sdk = getGlobalPushSdk()
        if (sdk) {
            return sdk
        }

        // Storage
        const settingsStore = new SettingsStorageManager()
        const userStore = new UserStorageManager(new LegacyStorageManager())
        const sessionStore = new SessionStorageManager()

        const emAsyncSettings = new EventManagerAsyncSettingsProvider(settingsStore, userStore)
        const eventManager = new SdkEventManager(emAsyncSettings)
        StaticEventDispatcher._setEventManager(eventManager)

        // Internal Implementation
        const sdkInternalImpl = new PushSdkInternalImpl(
            eventManager,
            settingsStore,
            userStore,
            new SessionManager(sessionStore),
            new DebugController(settingsStore, eventManager),
            sessionStore,
        )

        // Public Modules
        const userProfileModule = new UserProfileImpl(sdkInternalImpl)
        const eCommModule = new ECommImpl(userProfileModule)
        const notificationsModule = new PushNotificationsImpl(sdkInternalImpl)
        const promptsModule = new PromptsImpl(sdkInternalImpl)

        emAsyncSettings.setUserProfileModule(userProfileModule)

        // Public Implementation
        return new PushSdkPublicImpl(
            sdkInternalImpl,
            userProfileModule,
            eCommModule,
            notificationsModule,
            promptsModule,
        )
    }

    // region Namespaces

    public readonly UserProfile: UserProfile
    public readonly EComm: EComm
    public readonly PushNotifications: PushNotifications
    public readonly Prompts: Prompts

    // endregion

    // region Private Internals

    readonly #sdk: PushSdkInternalImpl
    readonly #logger: Logger
    // endregion
    // region Initializers

    protected constructor(
        sdk: PushSdkInternalImpl,
        userProfile: UserProfile,
        eComm: EComm,
        notifications: PushNotifications,
        prompts: Prompts,
        logger?: Logger,
    ) {
        this.#sdk = sdk
        this.#logger = logger ?? TaggedLogger._getInstance()
        this.UserProfile = userProfile
        this.EComm = eComm
        this.PushNotifications = notifications
        this.Prompts = prompts
    }

    public setConfiguration(config: PushSdkConfig) {
        this.#sdk._setConfiguration(config)
    }

    public getConfiguration(): PushSdkConfig | null {
        return this.#sdk.loadConfig ?? null
    }

    // endregion
    // region Public Property Accessors

    public get version(): string {
        return Globals._libraryVersion
    }

    public get iid(): string {
        return this.#sdk.iid
    }

    public get isLoaded(): boolean {
        return this.#sdk.isLoaded
    }

    // endregion
    // region Lifecycle Registrations

    public registerPushSDKLifecycleCallbacks(callbacks: PushSDKLifecycleCallbacks) {
        this.#sdk._registerPushSdkLifecycleCallbacks(callbacks)
    }

    public registerPermissionLifecycleCallbacks(callbacks: PermissionLifecycleCallbacks) {
        this.#sdk._registerPermissionLifecycleCallbacks(callbacks)
    }

    public registerAppMessageLifecycleCallbacks(callbacks: AppMessageLifecycleCallbacks) {
        this.#sdk._registerAppMessageLifecycleCallbacks(callbacks)
    }

    public registerPromptLifecycleCallbacks(callbacks: PromptLifecycleCallbacks) {
        this.#sdk._registerPromptLifecycleCallbacks(callbacks)
    }

    // endregion
    // region Logging

    public get logLevel(): LogLevel {
        return this.#logger.logLevel
    }

    public set logLevel(level: LogLevel) {
        this.#logger.logLevel = level
    }

    public async debug(display: boolean = true): Promise<void> {
        await this.#sdk._toggleDebugPanel(display)
    }

    // endregion
    // region Legacy
    // region PushlySDK.load

    public load(config: PushSdkConfig): void {
        this.setConfiguration(config)
    }

    // endregion
    // region PushlySDK.requestNotificationPermission

    public async requestNotificationPermission(promptId: number): Promise<void>
    public async requestNotificationPermission(): Promise<void>
    public async requestNotificationPermission(promptId?: number): Promise<void> {
        if (promptId === undefined) {
            this.PushNotifications.showPermissionPrompt()
        } else {
            this.PushNotifications.showPermissionPrompt(promptId)
        }
    }

    // endregion
    // region PushlySDK.showLogs | PushlySDK.getLogs

    public showLogs(show: boolean): void
    public showLogs(): void
    public showLogs(show: boolean = true): void {
        this.logLevel = show ? LogLevel.VERBOSE : LogLevel.NONE
    }

    public getLogs(): any[] {
        return this.#logger._getLogs()
    }

    // endregion
    // region Helpers

    public async isUserSoftUnsubscribed(): Promise<boolean> {
        return this.PushNotifications.getIsPaused()
    }

    public async isUserEligibleToPrompt(): Promise<boolean> {
        return this.PushNotifications.getIsEligibleToPrompt()
    }

    public async isUserSubscribed(): Promise<boolean> {
        return this.PushNotifications.getIsSubscribed()
    }

    public get context(): PushSdkContext {
        return this.#sdk._legacyContext
    }

    public getUser(): PushSdkContext['user'] {
        return this.context.user
    }

    // endregion
    // region PushlySDK.on | PushlySDK.trigger

    public on(event: PushSdkEvent, callback: PushSdkEventCallback): void {
        this.push([`on_${event}`, callback])
    }

    public watch(event: PushSdkEvent, callback: PushSdkEventCallback): void {
        this.on(event, callback)
    }

    public watchOnce(event: PushSdkEvent, callback: PushSdkEventCallback): void {
        this.on(event, callback)
    }

    public trigger(command: PushSdkCommand, data?: any): void {
        this.push([command, data] as any)
    }

    // endregion
    // region pushly() - Async Browser Commands
    // Generic Commands

    public push(args: [command: PushSdkCommand.LOAD, options: LoadCommandValue]): void
    public push(args: [command: PushSdkCommand.REQUEST_USER_DELETION]): void
    public push(args: [command: PushSdkCommand.OPT_OUT]): void
    public push(args: [command: PushSdkCommand.OPT_IN]): void
    public push(args: [command: PushSdkCommand.CONSENT, state: PushSdkConsentState]): void
    public push(args: [command: PushSdkCommand.DEBUG]): void

    /** @deprecated */
    public push(args: [command: PushSdkCommand.CONFIRM_CONSENT]): void

    // PushlySDK.UserProfile commands

    public push(args: [command: PushSdkCommand.EXTERNAL_ID, options: ExternalIdCommandValue]): void
    public push(args: [command: PushSdkCommand.DEREGISTER_EXTERNAL_ID]): void
    public push(args: [command: PushSdkCommand.PROFILE, options: ProfileCommandValue]): void
    public push(args: [command: PushSdkCommand.PROFILE_APPEND, options: ProfileAppendCommandValue]): void
    public push(args: [command: PushSdkCommand.PROFILE_REMOVE, options: ProfileRemoveCommandValue]): void
    public push(args: [command: PushSdkCommand.PAGE_TAG_VISIT, PageTagVisitCommandValue]): void

    // PushlySDK.PushNotifications commands

    public push(args: [command: PushSdkCommand.SHOW_PROMPT]): void
    public push(args: [command: PushSdkCommand.SHOW_PROMPT, options: ShowPromptCommandValue]): void
    public push(args: [command: PushSdkCommand.PAUSE_PUSH_NOTIFICATIONS]): void
    public push(args: [command: PushSdkCommand.RESUME_PUSH_NOTIFICATIONS]): void
    public push(args: [command: PushSdkCommand.GET_PUSH_NOTIFICATIONS_PAUSED_STATE, PushSdkEventCallback]): void
    public push(args: [command: PushSdkCommand.SUPPRESS_PROMPTS]): void
    public push(args: [command: PushSdkCommand.ALLOW_PROMPTS]): void
    public push(args: [command: PushSdkCommand.GET_PROMPTING_SUPPRESSED_STATE, GetPromptingSuppressedStateCallback]): void

    // PushlySDK.EComm commands

    public push(
        args: [command: PushSdkCommand.SAVE_ITEM, ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity],
    ): void
    public push(
        args: [
            command: PushSdkCommand.UNSAVE_ITEM,
            ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity,
        ],
    ): void
    public push(
        args: [command: PushSdkCommand.VIEW_ITEM, ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity],
    ): void
    public push(
        args: [
            command: PushSdkCommand.COMPLETE_ITEM,
            ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity,
        ],
    ): void
    public push(
        args: [
            command: PushSdkCommand.UNCOMPLETE_ITEM,
            ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity,
        ],
    ): void
    public push(
        args: [command: PushSdkCommand.RATE_ITEM, ECommProductRecordWithRating | ECommEventRecordWithRating],
    ): void
    public push(
        args: [
            command: PushSdkCommand.UNRATE_ITEM,
            ECommProductRecordWithoutQuantity | ECommEventRecordWithoutQuantity,
        ],
    ): void
    public push(args: [command: PushSdkCommand.ADD_TO_CART, ECommProductRecord | ECommEventRecord]): void
    public push(args: [command: PushSdkCommand.UPDATE_CART, ECommProductRecord | ECommEventRecord]): void
    public push(args: [command: PushSdkCommand.PURCHASE]): void
    public push(args: [command: PushSdkCommand.PURCHASE, ECommPurchaseRecord]): void
    public push(args: [command: PushSdkCommand.PURCHASE, ECommPurchaseDetails]): void

    // Events

    public push(args: [command: `on_${PushSdkEvent}`, PushSdkEventCallback], once?: boolean): void
    public push(args: PushArguments, once?: boolean): void {
        try {
            switch (args[0]) {
                // Generic Commands

                case PushSdkCommand.LOAD: {
                    this.setConfiguration(args[1])
                    break
                }

                case PushSdkCommand.REQUEST_USER_DELETION: {
                    this.UserProfile.requestUserDeletion()
                    break
                }

                case PushSdkCommand.OPT_OUT: {
                    this.UserProfile.optOut()
                    break
                }

                case PushSdkCommand.OPT_IN: {
                    this.UserProfile.optIn()
                    break
                }

                case PushSdkCommand.CONSENT: {
                    this.UserProfile.setConsent(args[1])
                    break
                }

                case PushSdkCommand.DEBUG: {
                    this.#sdk._toggleDebugPanel()
                    break
                }

                case PushSdkCommand.CONFIRM_CONSENT: {
                    // noop
                    break
                }

                // PushlySDK.UserProfile commands

                case PushSdkCommand.EXTERNAL_ID: {
                    this.UserProfile.setExternalId(args[1])
                    break
                }

                case PushSdkCommand.DEREGISTER_EXTERNAL_ID: {
                    this.UserProfile.unsetExternalId()
                    break
                }

                case PushSdkCommand.PROFILE: {
                    this.UserProfile.set(args[1])
                    break
                }

                case PushSdkCommand.PROFILE_APPEND: {
                    Object.entries(args[1]).forEach(([key, values]) => {
                        this.UserProfile.append(key, values)
                    })
                    break
                }

                case PushSdkCommand.PROFILE_REMOVE: {
                    Object.entries(args[1]).forEach(([key, values]) => {
                        this.UserProfile.remove(key, values)
                    })
                    break
                }

                case PushSdkCommand.PAGE_TAG_VISIT: {
                    const pageUrl = Env._location.href

                    let pageTags: string[] = []
                    if (Array.isArray(args[1])) {
                        pageTags = args[1]
                    } else {
                        try {
                            pageTags = [String(args[1])]
                        } catch {}
                    }

                    pageTags = pageTags
                        .filter((tag) => {
                            try {
                                return (
                                    tag !== undefined &&
                                    tag !== 'undefined' &&
                                    tag !== null &&
                                    tag !== 'null' &&
                                    !!String(tag).trim()
                                )
                            } catch {}

                            return false
                        })
                        .map((tag) => tag.toString())

                    this.#sdk._executeWhenConfiguredIfEnabled(() => {
                        this.#logger.verbose(
                            `Sending ${pageTags.length} tag${pageTags.length > 1 ? 's' : ''} for ${pageUrl}`,
                        )
                        this.#sdk.eventManager._track(TrackedEventAction.PAGE_TAG_VISIT, {
                            [Globals._tevDataPageUrlKey]: pageUrl,
                            [Globals._tevDataPageTagsKey]: pageTags,
                        })
                    })
                    break
                }

                // PushlySDK.PushNotifications commands

                case PushSdkCommand.SHOW_PROMPT: {
                    if (args.length === 1) {
                        this.PushNotifications.showPermissionPrompt()
                    } else {
                        const { id, checkEligibility } = args[1]

                        if (id !== undefined && id !== null) {
                            this.PushNotifications.showPermissionPrompt(id, {
                                skipEligibilityCheck: !checkEligibility,
                            })
                        } else {
                            this.PushNotifications.showPermissionPrompt({
                                skipEligibilityCheck: !checkEligibility,
                            })
                        }
                    }
                    break
                }

                case PushSdkCommand.PAUSE_PUSH_NOTIFICATIONS: {
                    this.PushNotifications.pause()
                    break
                }

                case PushSdkCommand.RESUME_PUSH_NOTIFICATIONS: {
                    this.PushNotifications.resume()
                    break
                }

                case PushSdkCommand.GET_PUSH_NOTIFICATIONS_PAUSED_STATE: {
                    this.PushNotifications.getIsPaused().then(args[1])
                    break
                }

                case PushSdkCommand.SUPPRESS_PROMPTS: {
                    this.PushNotifications.suppressPrompts()
                    break
                }

                case PushSdkCommand.ALLOW_PROMPTS: {
                    this.PushNotifications.allowPrompts()
                    break
                }

                case PushSdkCommand.GET_PROMPTING_SUPPRESSED_STATE: {
                    if (typeof args[1] === 'function') {
                        args[1](this.PushNotifications.getIsPromptingSuppressed())
                    }
                    break
                }

                // PushlySDK.EComm commands

                case PushSdkCommand.SAVE_ITEM: {
                    const records = 'products' in args[1] ? args[1].products : args[1].events
                    if (Array.isArray(records)) {
                        this.#sdk._executeWhenConfiguredIfEnabled(() => {
                            this.#logger.verbose(
                                `Adding ${records.length} item${records.length > 1 ? 's' : ''} to saved items`,
                            )
                            this.#sdk.eventManager._track(TrackedEventAction.SAVE_ITEM, args[1])
                        })
                    }
                    break
                }

                case PushSdkCommand.UNSAVE_ITEM: {
                    const records = 'products' in args[1] ? args[1].products : args[1].events
                    if (Array.isArray(records)) {
                        this.#sdk._executeWhenConfiguredIfEnabled(() => {
                            this.#logger.verbose(
                                `Removing ${records.length} item${records.length > 1 ? 's' : ''} from saved items`,
                            )
                            this.#sdk.eventManager._track(TrackedEventAction.UNSAVE_ITEM, args[1])
                        })
                    }
                    break
                }

                case PushSdkCommand.VIEW_ITEM: {
                    const records = 'products' in args[1] ? args[1].products : args[1].events
                    if (Array.isArray(records)) {
                        this.#sdk._executeWhenConfiguredIfEnabled(() => {
                            this.#logger.verbose(
                                `Adding ${records.length} item${records.length > 1 ? 's' : ''} to viewed items`,
                            )
                            this.#sdk.eventManager._track(TrackedEventAction.VIEW_ITEM, args[1])
                        })
                    }
                    break
                }

                case PushSdkCommand.COMPLETE_ITEM: {
                    const records = 'products' in args[1] ? args[1].products : args[1].events
                    if (Array.isArray(records)) {
                        this.#sdk._executeWhenConfiguredIfEnabled(() => {
                            this.#logger.verbose(
                                `Adding ${records.length} item${records.length > 1 ? 's' : ''} to completed items`,
                            )
                            this.#sdk.eventManager._track(TrackedEventAction.COMPLETE_ITEM, args[1])
                        })
                    }
                    break
                }

                case PushSdkCommand.UNCOMPLETE_ITEM: {
                    const records = 'products' in args[1] ? args[1].products : args[1].events
                    if (Array.isArray(records)) {
                        this.#sdk._executeWhenConfiguredIfEnabled(() => {
                            this.#logger.verbose(
                                `Removing ${records.length} item${records.length > 1 ? 's' : ''} from completed items`,
                            )
                            this.#sdk.eventManager._track(TrackedEventAction.UNCOMPLETE_ITEM, args[1])
                        })
                    }
                    break
                }

                case PushSdkCommand.RATE_ITEM: {
                    const records = 'products' in args[1] ? args[1].products : args[1].events
                    if (Array.isArray(records)) {
                        this.#sdk._executeWhenConfiguredIfEnabled(() => {
                            this.#logger.verbose(
                                `Adding ${records.length} item${records.length > 1 ? 's' : ''} to rated items`,
                            )
                            this.#sdk.eventManager._track(TrackedEventAction.RATE_ITEM, args[1])
                        })
                    }
                    break
                }

                case PushSdkCommand.UNRATE_ITEM: {
                    const records = 'products' in args[1] ? args[1].products : args[1].events
                    if (Array.isArray(records)) {
                        this.#sdk._executeWhenConfiguredIfEnabled(() => {
                            this.#logger.verbose(
                                `Removing ${records.length} item${records.length > 1 ? 's' : ''} from rated items`,
                            )
                            this.#sdk.eventManager._track(TrackedEventAction.UNRATE_ITEM, args[1])
                        })
                    }
                    break
                }

                case PushSdkCommand.ADD_TO_CART: {
                    const records = 'products' in args[1] ? args[1].products : args[1].events
                    if (Array.isArray(records)) {
                        this.#sdk._executeWhenConfiguredIfEnabled(() => {
                            this.#logger.verbose(
                                `Adding ${records.length} item${records.length > 1 ? 's' : ''} to cart`,
                            )
                            this.#sdk.eventManager._track(TrackedEventAction.ADD_TO_CART, args[1])
                        })
                    }
                    break
                }

                case PushSdkCommand.UPDATE_CART: {
                    const records = 'products' in args[1] ? args[1].products : args[1].events
                    if (Array.isArray(records)) {
                        this.#sdk._executeWhenConfiguredIfEnabled(() => {
                            if (!records.length) {
                                this.#logger.verbose(`Clearing cart`)
                            } else {
                                this.#logger.verbose(
                                    `Updating cart with ${records.length} item${records.length > 1 ? 's' : ''}.`,
                                )
                            }
                            this.#sdk.eventManager._track(TrackedEventAction.UPDATE_CART, args[1])
                        })
                    }
                    break
                }

                case PushSdkCommand.PURCHASE: {
                    if (args.length === 1) {
                        this.#logger.verbose('Tracking purchase')
                        this.#sdk.eventManager._track(TrackedEventAction.PURCHASE)
                    } else {
                        let records: ECommItem[] | undefined
                        if ('products' in args[1]) {
                            const normalized = args[1].products.map((item) => eCommItemOf(item, ECommItemType.PRODUCT))
                            const prepared = itemsWithQuantity(normalized) ? normalized : stripQuantity(normalized)
                            args[1].products = prepared
                            records = prepared as ECommItem[]
                        }
                        if ('events' in args[1]) {
                            const normalized = args[1].events.map((item) => eCommItemOf(item, ECommItemType.EVENT))
                            const prepared = itemsWithQuantity(normalized) ? normalized : stripQuantity(normalized)
                            args[1].events = prepared
                            records = prepared as ECommItem[]
                        }
                        if (!Array.isArray(records)) {
                            records = undefined
                        }

                        const sen = ['Tracking purchase']
                        if (records?.length) {
                            sen.push(`of ${records.length} item${records.length > 1 ? 's' : ''}`)
                        }
                        if ('purchase_id' in args[1]) {
                            sen.push(`Purchase ID: ${args[1].purchase_id}`)
                        }
                        if ('price_value' in args[1]) {
                            sen.push(`Total value: ${args[1].price_value}`)
                        }

                        this.#logger.verbose(sen.join(' '))
                        this.#sdk.eventManager._track(TrackedEventAction.PURCHASE, args[1])
                    }
                    break
                }

                // Event Handlers

                case `on_${PushSdkEvent.LOADED}`:
                case `on_${PushSdkEvent.WEB_PUSH_SUPPORTED}`:
                case `on_${PushSdkEvent.WEB_PUSH_UNSUPPORTED}`:
                case `on_${PushSdkEvent.READY}`:
                case `on_${PushSdkEvent.PROMPT_ELIGIBLE}`:
                case `on_${PushSdkEvent.PROMPT_INELIGIBLE}`:
                case `on_${PushSdkEvent.PROMPT_SHOWN}`:
                case `on_${PushSdkEvent.PROMPT_DISMISSED}`:
                case `on_${PushSdkEvent.PROMPT_ALLOWED}`:
                case `on_${PushSdkEvent.PERMISSION_SHOWN}`:
                case `on_${PushSdkEvent.PERMISSION_ALLOWED}`:
                case `on_${PushSdkEvent.PERMISSION_DENIED}`:
                case `on_${PushSdkEvent.PERMISSION_DISMISSED}`: {
                    const sdkEvent = args[0].replace(/^on_/, '') as PushSdkEvent

                    if (typeof args[1] === 'function') {
                        this.#sdk._registerLegacyEventCallback(sdkEvent, args[1], once)
                    } else {
                        this.#logger.warn('Invalid SDK event handler callback specified:', args)
                    }

                    break
                }

                default: {
                    if (!SuppressedAsyncCommands.includes(args[0]) && this.#sdk.debugController) {
                        this.#sdk.debugController._debugUnknownAsyncBrowserCommand(args)
                    }

                    this.#logger.warn('Unknown SDK command received:', args)
                }
            }
        } catch (err) {
            const error = ensureError(err)
            this.#logger.error(error)
            this.#sdk.eventManager._track(error)
        }
    }

    // endregion
    // endregion
}
